import { useState, useMemo, useEffect } from 'react';
import './StationCard.css';
import SideBoltIcon from '../../../assets/icons/side-bolt.svg?react';
import ChevronDownIcon from '../../../assets/icons/chevron-down.svg?react';
import { useNavigate } from 'react-router-dom';
import { useOpmContractNumberInfo } from '../../../hooks/queries/resourceRelationQueries';

const StationCard = ({
  station,
  onStationClick,
  drResourceSysId: drResourceSysIdProp,
  isSelected = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isSelected) {
      setIsExpanded(true);
    }
  }, [isSelected]);
  const handleToggle = (e) => {
    e.stopPropagation(); // 부모 클릭 이벤트 방지
    setIsExpanded(!isExpanded);
  };

  const handleCardClick = (e) => {
    e.stopPropagation(); // 이벤트 버블링 방지
    if (onStationClick) {
      onStationClick(station);
    }
  };

  const handleDetailClick = () => {
    navigate(`/station-management/detail/${station.StationSysId}`, { state: { from: 'map' } });
  };

  /** OPM API는 쉼표 구분 전체 번호를 넘기고 훅에서 분리·병합 호출 */
  const powerContractNumberRaw = useMemo(() => {
    const raw = station?.PowerMeterContractNumber ?? station?.PowerContractNumber ?? '';
    return String(raw).trim();
  }, [station]);

  const powerMeterContractDisplayLines = useMemo(() => {
    const raw = station?.PowerMeterContractNumber;
    if (raw == null || String(raw).trim() === '') return [];
    return String(raw)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    }, [station]);

  const drResourceSysId = station?.DrResourceSysId ?? drResourceSysIdProp ?? '';

  const opmParams = useMemo(
    () => ({
      DrResourceSysId: drResourceSysId,
      PowerContractNumber: powerContractNumberRaw || undefined,
      fetchEnabled: !!drResourceSysId && !!powerContractNumberRaw,
    }),
    [drResourceSysId, powerContractNumberRaw]
  );

  const { data: opmData, isPending } = useOpmContractNumberInfo(opmParams);

  const chargerCountDisplay = useMemo(() => {
    if (opmData != null && typeof opmData.SecondCount === 'number') {
      return opmData.SecondCount;
    }
    if (isPending) {
      return station.Chargers?.length ?? '…';
    }
    return station.Chargers?.length ?? 0;
  }, [opmData, isPending, station]);

  return (
    <div
      className={`station-card ${isExpanded ? 'expanded' : 'collapsed'}${isSelected ? ' is-selected' : ''}`}
      onClick={handleCardClick}
    >
      <div className="station-header">
        <div className="station-header-left">
          <div className="station-icon">
            <SideBoltIcon color='var(--primary-600)'/>
          </div>
          <div className="station-title">
            {station.CpoStationName}
          </div>
        </div>
        <button 
          className="station-toggle cursor-pointer"
          onClick={handleToggle}
        >
          <ChevronDownIcon color='var(--gray-600)' className={`toggle-icon ${isExpanded ? 'expanded' : ''}`}/>
        </button>
      </div>
      
      {isExpanded && (
        <div className="station-content">

          <div className="station-info-row">
            <span className="station-label one-row">주소</span>
            <span className="station-value">
              {station.Address}
            </span>
          </div>
          <div className="station-info-row">
            <span className="station-label one-row">충전사업자</span>
            <span className="station-value">
              {station.CpoName || '정보 없음'}
            </span>
          </div>
          <div className="station-info-row divide">
            <div className="grid grid-cols-2">
              <span className="station-label">계량기 계약번호</span>
              <span
                className={`station-value${powerMeterContractDisplayLines.length > 1 ? ' station-value--stacked' : ''}`}
              >
                {powerMeterContractDisplayLines.length > 0
                  ? powerMeterContractDisplayLines.map((line, i) => (
                      <span key={`${i}-${line}`} className="station-value-line">
                        {line}
                      </span>
                    ))
                  : '정보 없음'}
              </span>
            </div>
            <div className="grid grid-cols-2">
              <span className="station-label">충전소 식별자</span>
              <span className="station-value">
                {station.CpoStationId || '정보 없음'}
              </span>
            </div>
          </div>
          <div className="station-info-row divide">
            <div className="grid grid-cols-2">
              <span className="station-label">충전기 수</span>
              <span className="station-value charger-count">
                {chargerCountDisplay}
              </span>
            </div>
          </div>
          
          <div className='flex justify-end'>
            <button className='btn small-btn btn-primary' onClick={handleDetailClick}>
              <span className='text-white'>상세보기</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StationCard;
